import { useState, useEffect, useCallback, useRef } from 'react';
import {
  loadModule,
  getCachedModule,
  ModuleHttpError,
  DEFAULT_MODULE_LOAD_MAX_ATTEMPTS,
  isRetryableLoadError,
} from '../services/moduleLoader';
import type { MountableApp } from '@opengov/psp-contract';
import { useLoggerWithContext } from '@/services/logger/loggerContext';

/** sessionStorage key prefix for the once-per-module hard-reload guard (PSS-2668). */
const AUTO_RELOAD_GUARD_PREFIX = 'psp:autoReloaded:';

/**
 * Stable default reload. Hoisted to module scope (not an inline arrow in the
 * parameter default) so its identity never changes across renders — otherwise
 * it would invalidate `loadModuleFromUrl`'s `useCallback` every render and
 * re-fire the load effect on a loop.
 */
const defaultReload = () => window.location.reload();

/**
 * Test seams for `useModuleLoader`. Production callers (`RemoteAppShell`) pass
 * nothing and get the real `window.location.reload` / `sessionStorage` /
 * `Date.now`. Tests inject fakes to assert the guarded hard-reload behavior
 * without navigating jsdom.
 */
export interface UseModuleLoaderOptions {
  reload?: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

/**
 * Hook that loads a remote ES module from a URL, retrying transient failures
 * with exponential backoff via `loadModule()`.
 *
 * Successful loads are cached for the JS session by bare URL (see
 * `getCachedModule` / `loadModule`). When a module is already cached, this
 * hook commits it synchronously and skips the loading spinner. Concurrent
 * callers share the same in-flight promise.
 *
 * The hook owns an `AbortController` per invocation so that:
 *   - A URL change cancels the previous load's pending backoff and skips its
 *     subsequent attempts (the underlying dynamic `import()` cannot be
 *     canceled mid-flight, but we drop the stale resolution via the
 *     `isCurrentLoad()` guard rather than committing it to state).
 *   - Unmount cleanup aborts the in-flight load so a late settle does not
 *     trigger a React state-update warning.
 *
 * @param moduleUrl - The URL of the remote ES module to load, or null to clear
 * @returns Object with module, loading state, error, reload function, and
 *   retry-progress state (`loadAttempt` / `maxAttempts` for raw counts;
 *   `retryAttempt` / `maxRetries` for UI consumption, which counts retries
 *   only — see PSS-2536 §1).
 */
export function useModuleLoader(
  moduleUrl: string | null,
  { reload = defaultReload, storage = sessionStorage }: UseModuleLoaderOptions = {}
) {
  const logger = useLoggerWithContext();
  const [module, setModule] = useState<MountableApp | null>(() =>
    moduleUrl ? getCachedModule(moduleUrl) : null
  );
  const [loading, setLoading] = useState<boolean>(() => !!moduleUrl && !getCachedModule(moduleUrl));
  const [error, setError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState<number>(1);
  const [retryAttempt, setRetryAttempt] = useState<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const maxAttempts = DEFAULT_MODULE_LOAD_MAX_ATTEMPTS;
  const maxRetries = DEFAULT_MODULE_LOAD_MAX_ATTEMPTS - 1;

  const loadModuleFromUrl = useCallback(async () => {
    // Abort any in-flight previous load before starting a new one. Necessary
    // because the moduleUrl-change → useEffect path doesn't have a synchronous
    // cleanup step that fires before the new effect body runs.
    abortControllerRef.current?.abort();

    if (!moduleUrl) {
      abortControllerRef.current = null;
      setModule(null);
      setLoading(false);
      setError(null);
      setLoadAttempt(1);
      setRetryAttempt(0);
      return;
    }

    // Session cache hit: commit immediately and skip the loading spinner.
    const cached = getCachedModule(moduleUrl);
    if (cached) {
      abortControllerRef.current = null;
      setModule(cached);
      setLoading(false);
      setError(null);
      setLoadAttempt(1);
      setRetryAttempt(0);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setError(null);
    setLoadAttempt(1);
    setRetryAttempt(0);

    // Stale-completion guard. The underlying dynamic `import()` cannot be
    // canceled, so an old URL's load may still resolve/reject after a newer
    // URL has started. Every state commit + log call below must verify this
    // controller is still current before touching React state.
    const isCurrentLoad = () =>
      abortControllerRef.current === controller && !controller.signal.aborted;

    // Track the highest attempt number observed inside the closure so the
    // post-load logger.info / logger.error call records the truth rather than
    // reading a possibly-stale `loadAttempt` from a render snapshot.
    let attemptsObserved = 0;
    const onAttempt = (attempt: number) => {
      attemptsObserved = attempt;
      if (!isCurrentLoad()) return;
      setLoadAttempt(attempt);
      setRetryAttempt(Math.max(0, attempt - 1));
      // AC6: every attempt after the first is a cache-busted re-fetch; surface
      // it in Faro so we can confirm the fix is exercising and monitor recurrence.
      if (attempt > 1) {
        logger.warn('Module load retry', { moduleUrl, attempt, cacheBusted: true });
      }
    };

    // Key for the once-per-session hard-reload guard (PSS-2668). `moduleUrl` is
    // always the bare resolved URL — the cache-bust nonce is added internally by
    // loadModule and never surfaces here — so this key is stable across the bare
    // first attempt and every nonced retry, which is what stops a reload loop.
    const guardKey = `${AUTO_RELOAD_GUARD_PREFIX}${moduleUrl}`;

    const startTime = performance.now();

    try {
      const loadedModule = await loadModule(moduleUrl, {
        signal: controller.signal,
        retry: { onAttempt },
      });
      if (!isCurrentLoad()) return;

      // A successful load means any prior stale-deploy has recovered, so re-arm
      // the guard: a *future* redeploy in this same session should be allowed
      // its one hard reload again.
      storage.removeItem(guardKey);

      const loadingDuration = performance.now() - startTime;
      console.log(`[useModuleLoader] Module loaded in ${loadingDuration.toFixed(2)}ms`);
      logger.info('Module loaded successfully', {
        moduleUrl,
        attemptsUsed: attemptsObserved,
      });

      setModule(loadedModule);
    } catch (err) {
      // Cleanup- or URL-change-initiated abort, OR a stale completion racing
      // a newer load. Either way, do not commit error state or log this run
      // as a failure — the newer load (or hook unmount) owns the UI now.
      if (!isCurrentLoad() || (err instanceof DOMException && err.name === 'AbortError')) {
        return;
      }

      const error = err as Error;

      console.error('[useModuleLoader] Error:', error);

      // Faro payload. errorName distinguishes ModuleLoadTimeoutError and
      // ModuleHttpError from generic load failures; httpStatus + httpStatusText
      // only spread in for ModuleHttpError so dashboards can filter "5xx on AP"
      // separately from "syntax error" or "CORS denied". attemptsUsed records
      // how many loadModuleOnce() invocations the retry wrapper made before
      // surfacing this error; attemptsExhausted is true iff retry could have
      // helped but ran out of budget (vs. a non-retryable failure that bailed
      // on attempt 1).
      const attemptsExhausted = attemptsObserved === maxAttempts && isRetryableLoadError(error);

      logger.error('Module loading failed', {
        moduleUrl,
        errorName: error.name,
        error: error.message,
        stack: error.stack,
        attemptsUsed: attemptsObserved,
        attemptsExhausted,
        ...(error instanceof ModuleHttpError && {
          httpStatus: error.status,
          httpStatusText: error.statusText,
        }),
      });

      // AC4 — automatic recovery. A load is "recoverable by reload" when either:
      //   (a) cache-busting retries were exhausted on a retryable error — the
      //       stable-entry / missing-chunk case; a full reload re-bootstraps PSP
      //       with a clean module map and a fresh entry, OR
      //   (b) the entry file itself 404s — a renamed-entry redeploy that retries
      //       can't fix (every nonced re-fetch 404s too), but a reload picks up
      //       the new entry name from a fresh index.html.
      // Guarded to fire at most once per module per session so a genuinely-broken
      // or permanently-removed module (e.g. an S3 object that's truly gone) shows
      // the error page instead of reload-looping. Shape-validation errors are
      // non-retryable AND not 404, so they never reach this branch.
      const isEntry404 = error instanceof ModuleHttpError && error.status === 404;
      const recoverableByReload = attemptsExhausted || isEntry404;

      if (recoverableByReload && storage.getItem(guardKey) == null) {
        storage.setItem(guardKey, String(Date.now()));
        logger.error('Module load recovery: hard reload', {
          moduleUrl,
          errorName: error.name,
          attemptsUsed: attemptsObserved,
          ...(error instanceof ModuleHttpError && { httpStatus: error.status }),
        });
        reload();
        // The page is reloading; do not commit error state — the reload owns the
        // UI now and a transient error flash would be misleading.
        return;
      }

      setError(error);
      setModule(null); // Clear module on error to ensure clean state on retry
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  }, [moduleUrl, logger, maxAttempts, reload, storage]);

  useEffect(() => {
    loadModuleFromUrl();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [loadModuleFromUrl]);

  return {
    module,
    loading,
    error,
    reload: loadModuleFromUrl,
    loadAttempt,
    maxAttempts,
    retryAttempt,
    maxRetries,
  };
}
